import { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { NEW_IVR } from "@/constants/New";
import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputMedia from "@/design/forms/InputMedia";
import InputNumber from "@/design/forms/InputNumber";
import InputNumeric from "@/design/forms/InputNumeric";
import InputText from "@/design/forms/InputText";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viNumpad } from "@sippy-platform/valkyrie";

import { Container, Grid, IconButton, Stack, useMediaQuery, useTheme } from "@mui/material";

import ivrReducer from "./ivrReducer";
import OptionEditor from "./optionEditor/OptionEditor";

function Ivr() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const soundStore = useSelector((state) => state.domain.sounds);

  // Get general data
  useEffect(() => {
    Promise.resolve(Api.getSounds());
  }, []);

  const {
    value: ivr,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_IVR,
    reducer: ivrReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 40], { message: "Name must be at most 40 characters." }),
      exten: number()
        .required({ message: "Number is required." })
        .outside([100, 119], {
          message: "Number cannot be between 100 and 119.",
        })
        .outside([1, 9], { message: "Number cannot be between 1 and 9." })
        .not(0, { message: "Number cannot be 0." })
        .not(1711, { message: "Number cannot be 1711." })
        .not(1712, { message: "Number cannot be 1712." })
        .not(1722, { message: "Number cannot be 1722." })
        .not(1733, { message: "Number cannot be 1733." })
        .not(1813, { message: "Number cannot be 1813." }),
      inputTimeout: number()
        .required({ message: "Input time-out is required." })
        .min(1, { message: "Input time-out must be between 1 and 15." })
        .max(15, { message: "Input time-out must be between 1 and 15." }),
      repeat: number()
        .required({ message: "Repeat is required." })
        .min(0, { message: "Repeat must be between 0 and 5." })
        .max(5, { message: "Repeat must be between 0 and 5." }),
    }),
    onSubmit: () => Api.storeIvr(ivr),
    onSuccess: (response) => {
      if (!ivr.id) {
        dispatch({ type: "id", value: response.data.ivr.id });
        navigate(`../${response.data.ivr.id}`);
      }

      Promise.resolve(Api.getIvrs());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === ivr.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_IVR });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getIvr({ id: Number(id) }))
      .then((response) => !ignore && dispatch({ type: "set", value: response.data.ivr }))
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [dispatch, id, ivr.id]);

  // Handle changes
  const formHandler = (property, value) => {
    switch (property) {
      case "announcement":
      case "timeoutAnnouncement":
      case "invalidAnnouncement":
        dispatch({ type: property, value, soundStore });
        break;
      default:
        dispatch({ type: property, value });
    }

    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getIvrs()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${ivr.name || "IVR"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} /> <Avatar text={ivr.name} icon={viNumpad} />
              </IconButton>
            ) : (
              <Avatar text={ivr.name} icon={viNumpad} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={ivr.id || "nk"}
                isActionable={!!ivr?.id}
                onDeleteCheck={() => Api.deleteIvr(ivr, false)}
                onDelete={() => Api.deleteIvr(ivr, true)}
                name={`${ivr.name} (${ivr.exten})`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={ivr.name || "Unnamed IVR"}
          subheader={ivr.exten || "No number set"}
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={ivr.name} error={errors} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumber
              required
              id="exten"
              label="Number"
              onChange={formHandler}
              value={ivr.exten}
              error={errors}
              helperText="Number cannot be between 0 and 9 or any Belgian emergency number."
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Message">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumeric
              required
              id="inputTimeout"
              label="Input time-out"
              onChange={formHandler}
              value={ivr.inputTimeout}
              min={1}
              max={15}
              error={errors}
              helperText="Must be a value between 1 and 15."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumeric
              required
              id="repeat"
              label="Repeat"
              onChange={formHandler}
              value={ivr.repeat}
              min={0}
              max={5}
              error={errors}
              helperText="Must be a value between 0 and 5."
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Announcements">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputMedia
              hasEmpty={null}
              id="announcement"
              label="Announcement"
              onChange={formHandler}
              value={ivr.announcement?.id}
              selectValue="id"
              selects={soundStore.list}
              getOptionLabel={(option) => option.name}
              error={errors}
              getSourceDependency={(option) => option?.token}
              getSourceGeneration={(option) =>
                `${urls.audio}${pbx.context}%2F${option.id}.${option.contentType}?alt=media&token=${option.token}`
              }
              helperText="The announcement explaining the various options."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputMedia
              hasEmpty={null}
              id="timeoutAnnouncement"
              label="Time-out Announcement"
              onChange={formHandler}
              value={ivr.timeoutAnnouncement?.id}
              selectValue="id"
              selects={soundStore.list}
              getOptionLabel={(option) => option.name}
              error={errors}
              getSourceDependency={(option) => option?.token}
              getSourceGeneration={(option) =>
                `${urls.audio}${pbx.context}%2F${option.id}.${option.contentType}?alt=media&token=${option.token}`
              }
              helperText="The announcement when the input timeout has passed."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputMedia
              hasEmpty={null}
              id="invalidAnnouncement"
              label="Invalid Announcement"
              onChange={formHandler}
              value={ivr.invalidAnnouncement?.id}
              selectValue="id"
              selects={soundStore.list}
              getOptionLabel={(option) => option.name}
              error={errors}
              getSourceDependency={(option) => option?.token}
              getSourceGeneration={(option) =>
                `${urls.audio}${pbx.context}%2F${option.id}.${option.contentType}?alt=media&token=${option.token}`
              }
              helperText="The announcement when an input is given that is not set below."
            />
          </Grid>
        </FormGroup>
        <OptionEditor key={ivr.id} ivr={ivr} ivrDispatch={dispatch} />
        <FormGroup title="Overflow">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <OverflowDrawer
              label="Overflow"
              value={ivr.timeoutOverflow}
              propertyName="timeoutOverflow"
              overflowHandler={(value) => formHandler("timeoutOverflow", value)}
              filter={{ exten: [ivr.exten], custom: [], sound: [] }}
              helperText="Set the destination if the IVR does not pick up the call."
            />
          </Grid>
        </FormGroup>
      </Stack>
    </Container>
  );
}

export default Ivr;
